import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle, Spinner, X } from "@phosphor-icons/react";
import { getBillingStatus } from "@/lib/api";
import PublicNav from "@/components/PublicNav";

// Post-checkout landing page. Stripe redirects here with ?session_id=...
// We poll the backend status endpoint until payment_status === "paid" or we
// time out. On paid, the backend has already upgraded the user's tier.
export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const [state, setState] = useState("checking"); // checking | paid | failed | timeout
  const [info, setInfo] = useState(null);
  const cancelled = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let attempts = 0;
    const POLL_MS = 2000;
    const MAX_ATTEMPTS = 10;
    const tick = async () => {
      if (cancelled.current) return;
      attempts += 1;
      try {
        const data = await getBillingStatus(sessionId);
        setInfo(data);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (data.status === "expired") {
          setState("failed");
          return;
        }
        if (attempts >= MAX_ATTEMPTS) {
          setState("timeout");
          return;
        }
        setTimeout(tick, POLL_MS);
      } catch (_) {
        if (attempts >= MAX_ATTEMPTS) {
          setState("timeout");
          return;
        }
        setTimeout(tick, POLL_MS);
      }
    };
    tick();
    return () => {
      cancelled.current = true;
    };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-slate-50">
      <PublicNav />
      <section className="max-w-2xl mx-auto px-6 py-24 text-center">
        {state === "checking" && (
          <div data-testid="billing-checking">
            <Spinner size={42} weight="bold" className="mx-auto text-slate-400 animate-spin mb-6" />
            <h1 className="text-3xl font-display font-bold tracking-tight text-slate-950 mb-3">
              Confirming your payment…
            </h1>
            <p className="text-slate-600">Hang tight — Stripe is letting us know.</p>
          </div>
        )}
        {state === "paid" && (
          <div data-testid="billing-paid">
            <div className="inline-flex h-16 w-16 rounded-full bg-emerald-100 text-emerald-600 items-center justify-center mb-6">
              <CheckCircle size={36} weight="fill" />
            </div>
            <h1 className="text-3xl font-display font-bold tracking-tight text-slate-950 mb-3">
              You're upgraded.
            </h1>
            <p className="text-slate-600 mb-8">
              Welcome to <span className="font-semibold capitalize">{info?.tier || "your new plan"}</span>.
              The limits are off, the heatmap is unlocked, and we'll send a receipt by email.
            </p>
            <Button
              onClick={() => navigate("/dashboard")}
              className="h-12 px-8 rounded-full bg-slate-950 hover:bg-slate-800 text-white"
              data-testid="billing-go-dashboard"
            >
              Back to your charts
            </Button>
          </div>
        )}
        {state === "failed" && (
          <div data-testid="billing-failed">
            <div className="inline-flex h-16 w-16 rounded-full bg-rose-100 text-rose-600 items-center justify-center mb-6">
              <X size={36} weight="bold" />
            </div>
            <h1 className="text-3xl font-display font-bold tracking-tight text-slate-950 mb-3">
              Payment did not complete.
            </h1>
            <p className="text-slate-600 mb-8">No charge was made. Head back and try again.</p>
            <Link to="/pricing">
              <Button className="h-12 px-8 rounded-full bg-slate-950 hover:bg-slate-800 text-white">
                Back to pricing
              </Button>
            </Link>
          </div>
        )}
        {state === "timeout" && (
          <div data-testid="billing-timeout">
            <h1 className="text-2xl font-display font-bold tracking-tight text-slate-950 mb-3">
              We're still confirming your payment.
            </h1>
            <p className="text-slate-600 mb-8">
              Stripe is taking a moment. Check your email for a receipt, then refresh your
              dashboard. If your plan doesn't show as upgraded, contact us.
            </p>
            <Link to="/dashboard">
              <Button className="h-12 px-8 rounded-full bg-slate-950 hover:bg-slate-800 text-white">
                Go to dashboard
              </Button>
            </Link>
          </div>
        )}
      </section>
    </div>
  );
}
